/**
 * React hooks for interacting with $MANGO token contract
 */

import { useReadContract, useWriteContract, useWaitForTransactionReceipt } from 'wagmi'
import { CONTRACTS, MANGO_TOKEN_ABI, formatMango, parseMango } from '../contracts/mango'

/**
 * Hook to get MANGO token balance
 */
export function useMangoBalance(address?: `0x${string}`) {
  const { data, isLoading, error, refetch } = useReadContract({
    address: CONTRACTS.MangoToken as `0x${string}`,
    abi: MANGO_TOKEN_ABI,
    functionName: 'balanceOf',
    args: address ? [address] : undefined,
    query: {
      enabled: !!address && CONTRACTS.MangoToken !== '0x0000000000000000000000000000000000000000',
    },
  })

  return {
    balance: data as bigint | undefined,
    balanceFormatted: data ? formatMango(data as bigint) : '0',
    isLoading,
    error,
    refetch,
  }
}

/**
 * Hook to get MANGO token allowance
 */
export function useMangoAllowance(owner?: `0x${string}`, spender?: `0x${string}`) {
  const { data, isLoading, error, refetch } = useReadContract({
    address: CONTRACTS.MangoToken as `0x${string}`,
    abi: MANGO_TOKEN_ABI,
    functionName: 'allowance',
    args: owner && spender ? [owner, spender] : undefined,
    query: {
      enabled: !!owner && !!spender,
    },
  })

  return {
    allowance: data as bigint | undefined,
    allowanceFormatted: data ? formatMango(data as bigint) : '0',
    isLoading,
    error,
    refetch,
  }
}

/**
 * Hook to approve MANGO spending
 */
export function useMangoApprove() {
  const { writeContract, data: hash, isPending, error } = useWriteContract()
  
  const { isLoading: isConfirming, isSuccess } = useWaitForTransactionReceipt({
    hash,
  })

  const approve = async (spender: `0x${string}`, amount: bigint | number) => {
    const amountBigInt = typeof amount === 'bigint' ? amount : parseMango(amount)
    
    writeContract({
      address: CONTRACTS.MangoToken as `0x${string}`,
      abi: MANGO_TOKEN_ABI,
      functionName: 'approve',
      args: [spender, amountBigInt],
    })
  }

  return {
    approve,
    hash,
    isPending,
    isConfirming,
    isSuccess,
    error,
  }
}

/**
 * Hook to transfer MANGO tokens
 */
export function useMangoTransfer() {
  const { writeContract, data: hash, isPending, error } = useWriteContract()
  
  const { isLoading: isConfirming, isSuccess } = useWaitForTransactionReceipt({
    hash,
  })

  const transfer = async (to: `0x${string}`, amount: bigint | number) => {
    const amountBigInt = typeof amount === 'bigint' ? amount : parseMango(amount)
    
    writeContract({
      address: CONTRACTS.MangoToken as `0x${string}`,
      abi: MANGO_TOKEN_ABI,
      functionName: 'transfer',
      args: [to, amountBigInt],
    })
  }

  return {
    transfer,
    hash,
    isPending,
    isConfirming,
    isSuccess,
    error,
  }
}

/**
 * Hook to get MANGO token info
 */
export function useMangoTokenInfo() {
  const { data: totalSupply } = useReadContract({
    address: CONTRACTS.MangoToken as `0x${string}`,
    abi: MANGO_TOKEN_ABI,
    functionName: 'totalSupply',
  })

  const { data: totalBurned } = useReadContract({
    address: CONTRACTS.MangoToken as `0x${string}`,
    abi: MANGO_TOKEN_ABI,
    functionName: 'totalBurned',
  })

  const { data: maxSupply } = useReadContract({
    address: CONTRACTS.MangoToken as `0x${string}`,
    abi: MANGO_TOKEN_ABI,
    functionName: 'MAX_SUPPLY',
  })

  return {
    totalSupply: totalSupply as bigint | undefined,
    totalSupplyFormatted: totalSupply ? formatMango(totalSupply as bigint) : '0',
    totalBurned: totalBurned as bigint | undefined,
    totalBurnedFormatted: totalBurned ? formatMango(totalBurned as bigint) : '0',
    maxSupply: maxSupply as bigint | undefined,
    maxSupplyFormatted: maxSupply ? formatMango(maxSupply as bigint) : '1,000,000,000',
    circulatingSupply: totalSupply && totalBurned 
      ? (totalSupply as bigint) - (totalBurned as bigint) 
      : undefined,
  }
}

/**
 * Combined hook for common MANGO operations
 */
export function useMango(address?: `0x${string}`) {
  const balance = useMangoBalance(address)
  const tokenInfo = useMangoTokenInfo()
  const { approve, isPending: isApproving, isConfirming: isApproveConfirming, isSuccess: isApproveSuccess } = useMangoApprove()
  const { transfer, isPending: isTransferring, isConfirming: isTransferConfirming, isSuccess: isTransferSuccess } = useMangoTransfer()

  return {
    // Balance
    balance: balance.balance,
    balanceFormatted: balance.balanceFormatted,
    isLoadingBalance: balance.isLoading,
    refetchBalance: balance.refetch,
    
    // Token info
    ...tokenInfo,
    
    // Actions
    approve,
    transfer,
    
    // Status
    isApproving: isApproving || isApproveConfirming,
    isTransferring: isTransferring || isTransferConfirming,
    isApproveSuccess,
    isTransferSuccess,
  }
}
